import type { RefObject } from 'react';
import { useCallback, useMemo } from 'react';

import { convertSelectionToLegacyFormat } from '@confluence/highlight-actions';
import {
	BULK_ISSUE_CREATE_SIDE_PANEL_ID,
	BULK_ISSUE_CREATE_SIDE_PANEL_WIDTH,
	constructInsertStorageFragmentRequestPayload,
	getPageContentRange,
	getValidRange,
	useGetContentLastModifiedTimestamp,
	useIssueCreateSidePanelContext,
	useSetInsertStorageFragmentRequestPayload,
	useSetIsCreatedFromTable,
	useSetIsCreatedWithAi,
} from '@confluence/issue-create-side-panel';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { usePageInfo } from '@confluence/page-info';

type UseBulkIssueCreateProps = {
	contentId: string;
	content: RefObject<any>;
};

export const useBulkIssueCreate = ({ contentId, content }: UseBulkIssueCreateProps) => {
	const rightSidebarContext = useRightSidebarContext();
	const setIsCreatedWithAi = useSetIsCreatedWithAi();
	const setIsCreatedFromTable = useSetIsCreatedFromTable();
	const setInsertStorageFragmentRequestPayload = useSetInsertStorageFragmentRequestPayload();
	const contentLastModifiedTimestamp = useGetContentLastModifiedTimestamp();
	const [, issueCreateSidePanelActions] = useIssueCreateSidePanelContext();
	const { pageInfo } = usePageInfo({ fetchPolicy: 'cache-first' });

	const hasEditPermission = useMemo(
		() =>
			pageInfo?.operations?.some(
				(op) => op?.operation === 'update' && op?.targetType === pageInfo.type,
			) || false,
		[pageInfo],
	);

	return useCallback(() => {
		void setIsCreatedWithAi(true);
		void setIsCreatedFromTable(false);
		const range = getValidRange(getPageContentRange());
		const payload = constructInsertStorageFragmentRequestPayload(
			contentId,
			convertSelectionToLegacyFormat,
			range,
			content.current,
			contentLastModifiedTimestamp,
		);
		void setInsertStorageFragmentRequestPayload(payload);
		void issueCreateSidePanelActions.setHasEditPermission(hasEditPermission);
		rightSidebarContext.open(BULK_ISSUE_CREATE_SIDE_PANEL_ID, BULK_ISSUE_CREATE_SIDE_PANEL_WIDTH);
	}, [
		setIsCreatedWithAi,
		setIsCreatedFromTable,
		contentId,
		content,
		contentLastModifiedTimestamp,
		setInsertStorageFragmentRequestPayload,
		issueCreateSidePanelActions,
		hasEditPermission,
		rightSidebarContext,
	]);
};
